import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Umbrella, Shield, TrendingUp, Sparkles, Calculator, PiggyBank } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import BookingWidget from '../components/BookingWidget';

const PLANS = [
  {
    id: 'starter',
    name: 'Foundation',
    tag: 'For early savers',
    price: '€49',
    period: '/month',
    icon: PiggyBank,
    highlight: false,
    features: [
      'Personalized retirement roadmap',
      'Quarterly portfolio review',
      'Basic tax optimization',
      'Access to advisor via chat',
      'Digital dashboard & reports',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tag: 'Most popular',
    price: '€149',
    period: '/month',
    icon: TrendingUp,
    highlight: true,
    features: [
      'Everything in Foundation',
      'Dedicated wealth advisor',
      'Advanced tax & estate planning',
      'Monthly portfolio rebalancing',
      'Diversified global investments',
      'Retirement income projections',
    ],
  },
  {
    id: 'legacy',
    name: 'Legacy',
    tag: 'For high net worth',
    price: '€399',
    period: '/month',
    icon: Shield,
    highlight: false,
    features: [
      'Everything in Growth',
      'Private wealth management',
      'Trust & inheritance structuring',
      'Alternative investments access',
      'Cross-border tax coordination',
      'Dedicated concierge support',
    ],
  },
];

const FAQS = [
  { q: 'When is the best age to start retirement planning?', a: 'The earlier, the better. Starting in your 20s or 30s gives compound interest more time to work. That said, it\'s never too late — our advisors build tailored strategies at any age.' },
  { q: 'How is my money invested?', a: 'We use diversified, risk-adjusted portfolios across equities, bonds, real estate, and alternative assets, aligned with your risk tolerance and time horizon.' },
  { q: 'Can I change my plan later?', a: 'Absolutely. You can upgrade, downgrade, or pause your plan at any time from your dashboard — no penalties.' },
  { q: 'Is my money safe with Midbrothers?', a: 'Yes. All client assets are held in regulated custodian accounts under your name. Midbrothers only acts as advisor.' },
];

const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

const Retirement = () => {
  const [age, setAge] = useState(30);
  const [retireAge, setRetireAge] = useState(65);
  const [monthly, setMonthly] = useState(500);
  const [rate, setRate] = useState(7);

  const projection = useMemo(() => {
    const years = Math.max(0, retireAge - age);
    const months = years * 12;
    const r = rate / 100 / 12;
    const fv = monthly * ((Math.pow(1 + r, months) - 1) / (r || 1));
    const contributions = monthly * months;
    const growth = Math.max(0, fv - contributions);
    const monthlyIncome = (fv * 0.04) / 12; // 4% rule
    return { years, months, corpus: fv, contributions, growth, monthlyIncome };
  }, [age, retireAge, monthly, rate]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2c4d] via-[#0a2c4d] to-[#0a3b6a]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #0a6cb0 0%, transparent 50%), radial-gradient(circle at 80% 70%, #0a6cb0 0%, transparent 50%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-32 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-white/70 mb-4">RETIREMENT PLANNING</p>
            <h1 className="font-serif-display text-white text-5xl md:text-6xl font-bold leading-[1.05] mb-6">
              Secure Your Future, <br /> One Step at a Time.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed max-w-xl mb-8">
              Comprehensive retirement strategies designed for every stage of life. Build the wealth today
              that lets you retire on your own terms tomorrow.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#book" className="pill-btn">
                BOOK CONSULTATION <ArrowRight size={18} />
              </a>
              <a href="#calculator" className="pill-btn pill-btn-outline" style={{ background: 'transparent', color: '#ffffff', borderColor: '#ffffff' }}>
                USE CALCULATOR <Calculator size={18} />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: Umbrella, label: 'Plans', v: '3' },
              { icon: TrendingUp, label: 'Avg. Return', v: '7%+' },
              { icon: Shield, label: 'Advisors', v: '24/7' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/15 text-white">
                <s.icon size={22} className="mb-2 text-white/80" />
                <p className="text-3xl font-bold font-serif-display">{s.v}</p>
                <p className="text-xs uppercase tracking-wider text-white/70">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-24 bg-[#f6f9fc]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">CHOOSE YOUR PATH</p>
            <h2 className="section-title text-4xl md:text-5xl">Plans Built for Every Stage</h2>
            <p className="text-[#556a85] text-lg mt-4 max-w-2xl mx-auto">
              Transparent pricing. No hidden fees. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  className={`relative rounded-2xl p-8 border transition-all hover:-translate-y-1 ${
                    p.highlight
                      ? 'bg-[#0a2c4d] text-white border-[#0a6cb0] shadow-2xl'
                      : 'bg-white border-[#eef1f5] shadow-[0_10px_30px_-20px_rgba(10,44,77,0.2)] hover:shadow-xl'
                  }`}
                >
                  {p.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d4af37] text-[#0a2c4d] text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full flex items-center gap-1">
                      <Sparkles size={12} /> {p.tag}
                    </div>
                  )}
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${p.highlight ? 'bg-white/15 text-white' : 'bg-[#eaf3fb] text-[#0a6cb0]'}`}>
                    <Icon size={24} />
                  </div>
                  <p className={`text-xs uppercase tracking-wider font-semibold mb-2 ${p.highlight ? 'text-white/70' : 'text-[#0a6cb0]'}`}>{!p.highlight && p.tag}</p>
                  <h3 className={`font-serif-display text-3xl font-bold mb-2 ${p.highlight ? 'text-white' : 'text-[#0a2c4d]'}`}>{p.name}</h3>
                  <div className="flex items-end gap-1 mb-6">
                    <span className={`font-serif-display text-5xl font-bold ${p.highlight ? 'text-white' : 'text-[#0a2c4d]'}`}>{p.price}</span>
                    <span className={`text-sm mb-2 ${p.highlight ? 'text-white/70' : 'text-[#556a85]'}`}>{p.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${p.highlight ? 'bg-white/20 text-white' : 'bg-[#0a6cb0] text-white'}`}>
                          <Check size={12} />
                        </span>
                        <span className={`text-sm ${p.highlight ? 'text-white/85' : 'text-[#556a85]'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#book"
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm uppercase tracking-wider transition-colors ${
                      p.highlight
                        ? 'bg-white text-[#0a2c4d] hover:bg-[#eaf3fb]'
                        : 'bg-[#0a6cb0] text-white hover:bg-[#0a86d4]'
                    }`}
                  >
                    Get Started <ArrowRight size={16} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">RETIREMENT CALCULATOR</p>
            <h2 className="section-title text-4xl md:text-5xl">Project Your Future Wealth</h2>
            <p className="text-[#556a85] text-lg mt-4 max-w-2xl mx-auto">
              Move the sliders to see how small changes today compound into a bigger retirement fund tomorrow.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Inputs */}
            <div className="bg-[#f6f9fc] rounded-2xl p-8 border border-[#eef1f5]">
              <SliderRow label="Current age" value={age} min={18} max={70} step={1} unit=" yrs" onChange={setAge} />
              <SliderRow label="Retirement age" value={retireAge} min={Math.max(age + 1, 40)} max={80} step={1} unit=" yrs" onChange={setRetireAge} />
              <SliderRow label="Monthly contribution" value={monthly} min={50} max={5000} step={50} unit=" €" prefix onChange={setMonthly} />
              <SliderRow label="Expected annual return" value={rate} min={1} max={12} step={0.5} unit="%" onChange={setRate} />
            </div>

            {/* Result */}
            <div className="bg-gradient-to-br from-[#0a2c4d] to-[#0a3b6a] rounded-2xl p-8 text-white shadow-2xl">
              <p className="text-white/70 text-xs uppercase tracking-widest mb-2">Projected retirement fund</p>
              <p className="font-serif-display text-5xl md:text-6xl font-bold mb-2">
                €{fmt(projection.corpus)}
              </p>
              <p className="text-white/70 text-sm mb-8">at age {retireAge} · {projection.years} years of saving</p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <Stat label="Total contributions" value={`€${fmt(projection.contributions)}`} />
                <Stat label="Investment growth" value={`€${fmt(projection.growth)}`} accent />
                <Stat label="Monthly income" value={`€${fmt(projection.monthlyIncome)}`} sub="(4% withdrawal rule)" />
                <Stat label="Years to retire" value={`${projection.years} yrs`} />
              </div>

              <a href="#book" className="pill-btn w-full justify-center" style={{ background: '#ffffff', color: '#0a6cb0', borderColor: '#ffffff' }}>
                TALK TO AN ADVISOR <ArrowRight size={18} />
              </a>
              <p className="text-white/60 text-xs mt-4 text-center">
                * Estimates are for illustration only and do not constitute financial advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#f6f9fc]">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">QUESTIONS</p>
            <h2 className="section-title text-4xl md:text-5xl">Frequently Asked</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => <Faq key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Booking Widget */}
      <BookingWidget defaultPlan="" />

      {/* CTA Banner */}
      <section className="relative py-20 bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2c4d] via-[#0a2c4d]/95 to-[#0a3b6a]" />
        <div className="relative max-w-[1080px] mx-auto px-6 text-center">
          <h2 className="font-serif-display text-white text-3xl md:text-4xl font-bold mb-4">
            Ready to plan a worry-free retirement?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Book a free 30-minute consultation with a certified advisor and get a personalized roadmap.
          </p>
          <a href="#book" className="pill-btn">
            BOOK CONSULTATION <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const SliderRow = ({ label, value, min, max, step, unit, prefix, onChange }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between mb-2">
      <label className="text-[#0a2c4d] font-semibold text-sm">{label}</label>
      <span className="text-[#0a6cb0] font-bold text-lg font-serif-display">
        {prefix ? `€${fmt(value)}` : `${value}${unit}`}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-[#dbe4ee] accent-[#0a6cb0]"
    />
    <div className="flex justify-between text-xs text-[#8ea1b8] mt-1">
      <span>{prefix ? `€${fmt(min)}` : `${min}${unit}`}</span>
      <span>{prefix ? `€${fmt(max)}` : `${max}${unit}`}</span>
    </div>
  </div>
);

const Stat = ({ label, value, sub, accent }) => (
  <div className={`rounded-xl p-4 ${accent ? 'bg-[#0a6cb0]/25 border border-[#0a6cb0]/40' : 'bg-white/10 border border-white/10'}`}>
    <p className="text-white/70 text-[11px] uppercase tracking-wider mb-1">{label}</p>
    <p className="text-white font-bold text-xl font-serif-display">{value}</p>
    {sub && <p className="text-white/50 text-[11px] mt-1">{sub}</p>}
  </div>
);

const Faq = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-[#eef1f5] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#f9fbfd] transition-colors"
      >
        <span className="font-semibold text-[#0a2c4d]">{q}</span>
        <span className={`text-[#0a6cb0] text-2xl transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && <div className="px-5 pb-5 text-[#556a85] text-[15px] leading-relaxed">{a}</div>}
    </div>
  );
};

export default Retirement;
